import { View } from 'foundations-components/components/View';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FixedSizeList } from 'react-window';
import styled from 'styled-components';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import FilterToggle from './FilterOption';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const INPUT_HEIGHT = 32;
const MAX_HEIGHT = 192;

/*
 * Filter list of options if option text contains search word. For nested
 * option groups, only show matching nested options. If no nested options are
 * found, exclude the entire group.
 */
function searchOptions(options, search) {
  if (!search.length) return options;
  const searchRegex = new RegExp(search, 'i');
  return options.reduce((os, option) => {
    if ('options' in option) {
      const nestedOptions = searchOptions(option.options, search);
      if (nestedOptions.length) os.push(Object.assign({}, option, {
        options: nestedOptions
      }));
    } else if (option.text && searchRegex.test(option.text)) {
      os.push(option);
    }
    return os;
  }, []);
}
const VirtualizedRow = styled.div.withConfig({
  displayName: "FilterToggleGroup__VirtualizedRow"
})(["position:absolute;top:", "px;left:0;width:100%;height:", "px;"], ({
  top
}) => top, INPUT_HEIGHT);
function FilterToggleGroup({
  ariaLabel,
  disabled,
  inputClassName,
  labelComponent: LabelComponent,
  maxHeight = MAX_HEIGHT,
  multi,
  name,
  onChange,
  onSearchBlur,
  onSearchChange,
  options: defaultOptions,
  searchable,
  searchPlaceholder,
  scrollable,
  value,
  virtualized
}) {
  const [options, setOptions] = useState(defaultOptions);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounceValue(search);
  useEffect(() => {
    if (!debouncedSearch) {
      setOptions(defaultOptions);
      return;
    }
    const filteredOptions = searchOptions(defaultOptions, debouncedSearch);
    setOptions(filteredOptions);
  }, [debouncedSearch, defaultOptions, setOptions]);
  const renderOption = useCallback((option, index) => {
    return /*#__PURE__*/_jsx(FilterToggle, {
      disabled: disabled,
      forceOpen: !!debouncedSearch,
      inputClassName: inputClassName,
      LabelComponent: LabelComponent,
      multi: !!multi,
      name: name,
      onChange: onChange,
      option: option,
      value: value
    }, index);
  }, [LabelComponent, debouncedSearch, disabled, inputClassName, multi, name, onChange, value]);
  const renderedToggleGroup = useMemo(() => /*#__PURE__*/_jsx(ToggleGroup, {
    "aria-label": ariaLabel,
    name: name,
    role: "group",
    children: virtualized ? /*#__PURE__*/_jsx(FixedSizeList, {
      height: MAX_HEIGHT,
      itemCount: options.length,
      itemData: options,
      itemSize: INPUT_HEIGHT,
      width: "100%",
      children: ({
        data,
        index,
        style
      }) =>
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(VirtualizedRow, {
        top: style.top,
        children: renderOption(data[index], index)
      })
    }) : options.map(renderOption)
  }), [ariaLabel, name, options, renderOption, virtualized]);
  const handleSearchChange = event => {
    setSearch(event.target.value);
    if (onSearchChange) onSearchChange(event);
  };
  const renderedSearchInput = searchable && /*#__PURE__*/_jsx(SearchInput, {
    onBlur: onSearchBlur,
    onChange: handleSearchChange,
    placeholder: searchPlaceholder,
    value: search,
    marginBlockEnd: "100"
  });
  if (scrollable && !virtualized) {
    return /*#__PURE__*/_jsxs(View, {
      paddingBlockEnd: "200",
      children: [renderedSearchInput, /*#__PURE__*/_jsx(ScrollContainer, {
        gradientColor: "fill-surface-default",
        maxHeight: maxHeight,
        size: 28,
        children: renderedToggleGroup
      })]
    });
  }
  return /*#__PURE__*/_jsxs(View, {
    paddingBlockEnd: "200",
    children: [renderedSearchInput, renderedToggleGroup]
  });
}
export default FilterToggleGroup;